import { Component } from '@theme/component';

/**
 * Wishlist ('favoris') button, positioned as an overlay over a product image (grid card or
 * product-page gallery). Persists to localStorage - no customer account required. See
 * @theme/wishlist-page for the page that reads this list back out.
 */

const STORAGE_KEY = 'wishlist:handles';
const CHANGE_EVENT = 'wishlist:change';

/**
 * Reads the wishlist handle list from localStorage.
 * @returns {string[]} Empty array on parse failure or when storage is unavailable (private
 *   browsing, quota exceeded, disabled by the browser) - the button still works, it just won't persist.
 */
function readWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** @param {string[]} handles */
function writeWishlist(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch {
    // Storage unavailable - fail silently, matches readWishlist()'s fallback.
  }
}

export class WishlistButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    window.addEventListener(CHANGE_EVENT, this.#onExternalChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(CHANGE_EVENT, this.#onExternalChange);
  }

  /** @param {Event} event */
  toggle(event) {
    event.preventDefault();
    const handle = this.dataset.productHandle;
    if (!handle) return;

    const handles = readWishlist();
    const index = handles.indexOf(handle);
    const isNowActive = index === -1;

    if (isNowActive) {
      handles.push(handle);
    } else {
      handles.splice(index, 1);
    }

    writeWishlist(handles);
    this.#applyState(isNowActive);

    // Lets every other button for the same product (e.g. the same item shown in a
    // recommendations row) and the wishlist page (if open in another tab) stay in sync.
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handle, active: isNowActive } }));
  }

  /** @param {CustomEvent<{handle: string, active: boolean}>} event */
  #onExternalChange = (event) => {
    if (event.detail?.handle !== this.dataset.productHandle) return;
    this.#applyState(event.detail.active);
  };

  #sync() {
    const handle = this.dataset.productHandle;
    if (!handle) return;
    this.#applyState(readWishlist().includes(handle));
  }

  /** @param {boolean} active */
  #applyState(active) {
    const button = this.refs.button;
    if (!(button instanceof HTMLElement)) return;
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', active ? 'Retirer des favoris' : 'Ajouter aux favoris');
    this.classList.toggle('is-active', active);
  }
}

if (!customElements.get('wishlist-button-component')) {
  customElements.define('wishlist-button-component', WishlistButton);
}
